import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useAccess } from "../context/AccessContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Camera, Trash2, Flame, Clock, Users, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import TierBadge from "../components/TierBadge";
import PremiumAchievementsSection from "../components/PremiumAchievementsSection";
import { usePremiumStatus, formatExpiryDate } from "../hooks/usePremiumStatus";

export default function Profile() {
  const { user, refresh } = useAuth();
  const { access, refresh: refreshAccess, effectivePremium } = useAccess();
  const premium = usePremiumStatus();  // canonical plan state (Premium wins over Trial)
  const [form, setForm] = useState({
    name: user?.name || "",
    username: user?.username || "",
    bio: user?.bio || "",
  });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [stats, setStats] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/users/me/stats").then((r) => setStats(r.data)).catch(() => {}); }, [user]);

  // 🛡️ Sync Premium Status — for the rare case where a user paid but their
  // status is stuck on Trial (webhook wasn't configured yet, /premium/verify
  // never fired, etc.). Server checks Razorpay live and activates if a
  // captured payment exists. Fully idempotent — safe to click repeatedly.
  const syncPremium = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/premium/self-heal");
      await Promise.all([refresh().catch(() => {}), refreshAccess().catch(() => {})]);
      if (data.fixed && data.fixed.length > 0) {
        toast.success(`Premium restored! Renews ${formatExpiryDate(data.fixed[0].premium_until)}`);
      } else if (data.already_ok && data.already_ok.length > 0) {
        toast.success("Your premium is already active — refreshed just in case.");
      } else if (data.still_pending && data.still_pending.length > 0) {
        toast.info("No captured payment found. If you just paid, wait 60s and try again.");
      } else {
        toast.info("No recent payments found on your account.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sync failed. Please try again.");
    } finally {
      setSyncing(false);
    }
  };

  const onAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2_500_000) { toast.error("Image too large (max 2MB)"); return; }
    const r = new FileReader();
    r.onload = async () => {
      try {
        await api.post("/users/me/avatar", { image_base64: r.result });
        await refresh();
        toast.success("Profile picture updated");
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    r.readAsDataURL(f);
  };

  const removeAvatar = async () => {
    await api.delete("/users/me/avatar");
    await refresh();
    toast.success("Profile picture removed");
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/users/me", form);
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-root">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-600">Profile</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Your profile</h1>
      </div>

      <div className="flex items-center gap-4 rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
        <div className="relative">
          {user?.picture ? (
            <img src={user.picture} className="h-20 w-20 rounded-full object-cover" alt="avatar"/>
          ) : (
            <div className="grid h-20 w-20 place-items-center rounded-full bg-emerald-500 text-2xl font-semibold text-white">{user?.name?.[0]?.toUpperCase()}</div>
          )}
          <button onClick={() => fileRef.current?.click()} data-testid="upload-avatar-btn" className="absolute -bottom-1 -right-1 grid h-7 w-7 place-items-center rounded-full border-2 border-white bg-emerald-500 text-white dark:border-zinc-900"><Camera size={12}/></button>
          <input type="file" ref={fileRef} accept="image/jpeg,image/png,image/webp" onChange={onAvatar} className="hidden"/>
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 font-heading text-xl font-semibold">
            <span>{user?.name}</span>
            {/* Full-label variant shows "Syllox Premium" (self-brand). Uses
                the unified `premium` hook so Premium always wins over Trial
                and the badge never lags after payment. */}
            <TierBadge
              tier={premium.tier}
              daysLeft={premium.isTrial ? premium.daysLeft : undefined}
              fullLabel
              testid="profile-tier-badge"
            />
          </div>
          <div className="text-sm text-zinc-500">@{user?.username} · {user?.email || (user?.phone ? `+91 ${user.phone}` : "")}</div>
          <div className="mt-1 text-xs uppercase tracking-[0.18em] text-zinc-500" data-testid="profile-plan-subtitle">
            {premium.isPaid
              ? (premium.expiresAt
                  ? `Syllox Premium · Renews ${formatExpiryDate(premium.expiresAt)}`
                  : "Syllox Premium")
              : premium.isTrial
                ? `Trial member · 30-day access${premium.daysLeft != null ? ` · ${premium.daysLeft} day${premium.daysLeft === 1 ? "" : "s"} left` : ""}`
                : "Free plan"}
          </div>
        </div>
        {user?.picture && (
          <Button variant="ghost" size="icon" onClick={removeAvatar} data-testid="remove-avatar-btn" title="Remove picture">
            <Trash2 size={16} className="text-zinc-400 hover:text-red-500"/>
          </Button>
        )}
      </div>

      {/* 🛡️ Sync Premium Status — recovery UI for the rare stuck-payment case.
          Only shown when the user is NOT currently paid. Hits /premium/self-heal
          which live-checks Razorpay and activates if a captured payment exists.
          Invisible for paid users — no need to nag them. */}
      {!premium.isPaid && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-950/20" data-testid="sync-premium-card">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-semibold text-amber-900 dark:text-amber-200">Recently paid but still showing Trial?</div>
              <div className="mt-0.5 text-xs text-amber-800/80 dark:text-amber-200/70">
                Force-sync your Premium status with Razorpay. If a completed payment exists on your account, it will activate instantly.
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={syncPremium}
              disabled={syncing}
              data-testid="sync-premium-btn"
              className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-transparent dark:text-amber-100"
            >
              <RefreshCw size={14} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync Premium status"}
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3" data-testid="profile-stats">
        {[
          { icon: Users, label: "Friends", value: stats?.friends ?? "—", color: "text-blue-600" },
          { icon: Flame, label: "Streak", value: `${stats?.streak ?? 0}d`, color: "text-amber-600" },
          { icon: Clock, label: "Total hours", value: `${stats?.total_hours ?? 0}h`, color: "text-emerald-600" },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
            <div className={`mb-1 ${s.color}`}><s.icon size={16}/></div>
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">{s.label}</div>
            <div className="mt-1 font-heading text-xl font-bold">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Premium-only Achievements section — gated on effectivePremium so paid
          users, founder reward users, and trial users all see it; free users
          (incl. expired trial) see nothing. Badge history is preserved in the
          DB regardless; when premium returns the section restores itself. */}
      {effectivePremium && <PremiumAchievementsSection />}

      <form onSubmit={save} className="grid grid-cols-1 gap-4 rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="p-name">Name</Label>
          <Input id="p-name" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="profile-name-input"/>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="p-username">Username</Label>
          <Input id="p-username" value={form.username} onChange={(e)=>setForm({...form, username: e.target.value})} data-testid="profile-username-input"/>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="p-bio">Bio</Label>
          <Textarea id="p-bio" value={form.bio} onChange={(e)=>setForm({...form, bio: e.target.value})} placeholder="A line about you" data-testid="profile-bio-input"/>
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="profile-save-btn">{saving ? "Saving…" : "Save changes"}</Button>
        </div>
      </form>
    </div>
  );
}
